import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Grid, IconButton, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import Table from "../shared/Table";
import { useHistory } from "react-router-dom";
import { useSnackbar } from "notistack";
import { useAppContext } from "../../api/AppContext";
import { useModals } from "../../hooks/useModals";

const hasWriteAccess = (accessType) =>
  Array.isArray(accessType)
    ? accessType.includes("write")
    : accessType === "write";

const displayAccessType = (accessType) =>
  hasWriteAccess(accessType) ? "write" : "read";

const ImageGalleries = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const {
    listImageGalleries,
    deleteImageGallery,
    updateImageGallery,
    user,
    fetchMyAuthCompanies,
  } = useAppContext();
  const DIGITALATTITUDE_COMPANY_ID = "5706867a-c563-4ca5-b803-c1883c38b640";
  const isDAUser = !!(
    user &&
    Array.isArray(user.companies) &&
    user.companies.includes(DIGITALATTITUDE_COMPANY_ID)
  );

  const [galleries, setGalleries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const modals = useModals();

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const fromApi = await listImageGalleries();
      const source = Array.isArray(fromApi) ? fromApi : [];

      setGalleries(
        source.map((g) => {
          const companies = Array.isArray(g.companies) ? g.companies : [];
          const accessTypesFromUnified = companies
            .map((c) => c && c.accessType)
            .filter(Boolean);

          return {
            id: g.id,
            name: g.name,
            description: g.description || null,
            createdAt: g.created_at || g.createdAt || null,
            imageCount: g.image_count ?? g.imageCount ?? 0,
            accessType: accessTypesFromUnified,
            companies,
            fieldIds: g.field_ids || g.fieldIds || [],
            fields: g.fields || [],
            isPublished: g.is_published ?? g.isPublished ?? false,
          };
        })
      );
    } catch (e) {
      console.error("Failed to load galleries", e);
      enqueueSnackbar(e?.message || "Failed to load galleries", {
        variant: "error",
      });
      setGalleries([]);
    } finally {
      setIsLoading(false);
    }
  }, [enqueueSnackbar, listImageGalleries]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const [companiesLocal, setCompaniesLocal] = useState([]);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const list = await fetchMyAuthCompanies?.();
        if (mounted) setCompaniesLocal(Array.isArray(list) ? list : []);
      } catch (e) {
        console.error("Failed to load my auth companies", e);
      }
    };
    if (!companiesLocal?.length) load();
    return () => {
      mounted = false;
    };
  }, [fetchMyAuthCompanies]);

  const companiesById = useMemo(() => {
    const map = new Map();
    (companiesLocal || []).forEach((c) => {
      const name = c?.name || c?.cname || c?.displayName || c?.id;
      if (c?.id) map.set(c.id, name);
    });
    return map;
  }, [companiesLocal]);

  const handleCreate = useCallback(() => {
    history.push(`/image-galleries/new/start`, { isNew: true });
  }, [history]);

  const handleEdit = useCallback(
    ({ id }) => {
      if (id) history.push(`/image-galleries/${id}`);
    },
    [history]
  );

  const handleDelete = useCallback(
    async ({ id, name }) => {
      if (!id) return;
      const doDelete = async () => {
        try {
          await deleteImageGallery({ galleryId: id });
          setGalleries((prev) => prev.filter((g) => g.id !== id));
          enqueueSnackbar(`Gallery "${name || id}" deleted`, {
            variant: "success",
          });
        } catch (e) {
          enqueueSnackbar(e?.message || "Failed to delete gallery", {
            variant: "error",
          });
        }
      };

      // Ask for confirmation before deleting
      modals.openConfirmation({
        title: "Delete gallery?",
        text: `Are you sure you want to delete the gallery "${
          name || id
        }"? This action cannot be undone.`,
        confirmCTATitle: "Delete",
        closeCTATitle: "Cancel",
        onConfirm: doDelete,
      });
    },
    [deleteImageGallery, enqueueSnackbar, modals]
  );

  const handleTogglePublish = useCallback(
    async (row) => {
      const next = !row.isPublished;
      const doToggle = async () => {
        try {
          await updateImageGallery({ galleryId: row.id, isPublished: next });
          setGalleries((prev) =>
            prev.map((g) => (g.id === row.id ? { ...g, isPublished: next } : g))
          );
          enqueueSnackbar(
            next
              ? "Gallery published successfully!"
              : "Gallery unpublished successfully!",
            { variant: "success" }
          );
        } catch (e) {
          enqueueSnackbar("Error updating gallery status", {
            variant: "error",
          });
        }
      };

      if (next) {
        modals.openConfirmation({
          title: "Publish gallery?",
          text: `Do you want to publish the gallery "${row.name}"?`,
          confirmCTATitle: "Publish",
          onConfirm: doToggle,
        });
      } else {
        await doToggle();
      }
    },
    [enqueueSnackbar, modals, updateImageGallery]
  );

  const columns = useMemo(
    () => [
      { title: "Name", field: "name" },
      {
        title: "Description",
        field: "description",
        render: (rowData) => rowData.description || "",
      },
      {
        title: "Images",
        field: "imageCount",
        render: (rowData) => rowData.imageCount ?? 0,
        defaultSort: "desc",
        type: "numeric",
      },
      {
        title: "Created At",
        field: "createdAt",
        render: (rowData) =>
          rowData.createdAt ? new Date(rowData.createdAt).toLocaleString() : "",
      },
      {
        title: "Access Type",
        field: "accessType",
        render: (rowData) => displayAccessType(rowData.accessType),
      },
      {
        title: "Disabled Fields",
        field: "fieldNames",
        render: (rowData) => {
          const names =
            Array.isArray(rowData.fields) && rowData.fields.length
              ? rowData.fields
                  .map((f) => (f && (f.name || f.id)) || null)
                  .filter(Boolean)
                  .join(", ")
              : Array.isArray(rowData.fieldIds) && rowData.fieldIds.length
              ? rowData.fieldIds.join(", ")
              : "";
          return <span title={names}>{names}</span>;
        },
        searchable: true,
      },
      {
        title: "Companies",
        field: "companies",
        render: (rowData) => {
          const list = (
            Array.isArray(rowData.companies) ? rowData.companies : []
          ).map((c) => companiesById.get(c.id) || c.id);
          const names = list.join(", ");
          return <span title={names}>{names}</span>;
        },
        searchable: true,
      },
      {
        title: "Published",
        field: "isPublished",
        render: (rowData) => {
          const writable = isDAUser || hasWriteAccess(rowData?.accessType);
          const title = rowData.isPublished
            ? writable
              ? "Unpublish"
              : "No permission"
            : writable
            ? "Publish"
            : "No permission";
          return (
            <Box alignItems="center" display="flex">
              <Tooltip title={title}>
                <span>
                  <IconButton
                    aria-label="toggle publish"
                    disabled={!writable}
                    size="small"
                    onClick={() => handleTogglePublish(rowData)}
                  >
                    {rowData.isPublished ? (
                      <VisibilityIcon
                        style={{ color: writable ? "green" : undefined }}
                      />
                    ) : (
                      <VisibilityOffIcon
                        style={{ color: writable ? "red" : undefined }}
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
      {
        title: "Actions",
        field: "actions",
        sorting: false,
        render: (rowData) => {
          const writable = isDAUser || hasWriteAccess(rowData?.accessType);
          return (
            <Box alignItems="center" display="flex" style={{ gap: 8 }}>
              <Tooltip
                placement="top"
                title={writable ? "Edit images" : "No permission"}
              >
                <span>
                  <IconButton
                    aria-label="Edit images"
                    disabled={!writable}
                    size="small"
                    onClick={() => {
                      if (!writable) return;
                      const gid = rowData?.id;
                      if (gid) {
                        modals.openModal(
                          modals.modalTypes.FilteredGalleryImagePreview,
                          {
                            galleryIds: [String(gid)],
                            initialSelectedGalleryIds: [String(gid)],
                          }
                        );
                      }
                    }}
                  >
                    <PhotoLibraryIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                placement="top"
                title={writable ? "Edit gallery config" : "No permission"}
              >
                <span>
                  <IconButton
                    aria-label="Edit gallery config"
                    disabled={!writable}
                    size="small"
                    onClick={() => writable && handleEdit(rowData)}
                  >
                    <EditIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                placement="top"
                title={writable ? "Delete gallery" : "No permission"}
              >
                <span>
                  <IconButton
                    aria-label="delete gallery"
                    disabled={!writable}
                    size="small"
                    onClick={() => writable && handleDelete(rowData)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [
      companiesById,
      handleTogglePublish,
      handleEdit,
      handleDelete,
      isDAUser,
      modals,
    ]
  );

  const tableActions = useMemo(
    () => [
      <Button
        key="add"
        color="primary"
        endIcon={<AddIcon />}
        variant="contained"
        onClick={handleCreate}
      >
        Add gallery
      </Button>,
    ],
    [handleCreate]
  );

  const options = useMemo(
    () => ({ pageSize: 10, pageSizeOptions: [5, 10, 15], search: true }),
    []
  );

  return (
    <Box
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      height="100%"
      p={4}
      width="100%"
    >
      <Grid container direction="column" spacing={4}>
        <Grid item>
          <Table
            columns={columns}
            data={galleries}
            isLoading={isLoading}
            localization={{ emptyDataSourceMessage: "No galleries yet" }}
            options={options}
            refetch={refresh}
            tableActions={tableActions}
            title="Galleries"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ImageGalleries;
